import { createFileRoute, Link } from "@tanstack/react-router";

import { PageChrome } from "../components/PageChrome";
import { PASSAGES, passageMinutes, passagePath } from "../lib/passage";
import {
  breadcrumbJsonLd,
  canonical,
  collectionJsonLd,
  documentHeaders,
  socialMeta,
} from "../lib/seo";
import { VERSE_TOPICS, versePath } from "../lib/verses";

const TITLE = "Famous Bible verses, explained — Bible Shield";
const DESCRIPTION =
  "The eight most-read passages in English Christianity, each with its context printed and a section on what it is usually taken to mean and does not.";

/**
 * `/verse` — the hub for single passages.
 *
 * ── Why the set is what it is ────────────────────────────────────────────────
 *
 * `docs/BIBLE-SEARCH-DEMAND.md` has the working. The eight below are the
 * overlap between the two largest published rankings: YouVersion's app data,
 * where Isaiah 41:10 has been the year's most-read text four times in six, and
 * Bible Gateway's web data, whose entire 2025 top six was Psalm 23 read verse
 * by verse. Nothing here was chosen because we liked it.
 *
 * ── Why it is a separate section from /verses ────────────────────────────────
 *
 * Different question. Somebody typing a topic wants ten passages; somebody
 * typing a reference wants one, in context, with the popular misreading dealt
 * with. Serving both from a topical list serves neither, and the two hubs link
 * to each other for anybody who arrives at the wrong one.
 */
export const Route = createFileRoute("/verse/")({
  headers: documentHeaders,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...socialMeta({
        title: TITLE,
        description: DESCRIPTION,
        path: "/verse",
        lang: "en",
      }),
      {
        "script:ld+json": breadcrumbJsonLd([
          { name: "Bible Shield", path: "/" },
          { name: "Passages", path: "/verse" },
        ]),
      },
      {
        "script:ld+json": collectionJsonLd({
          name: TITLE,
          description: DESCRIPTION,
          path: "/verse",
          items: PASSAGES.map((p) => ({
            name: p.h1,
            path: passagePath(p.slug),
          })),
        }),
      },
    ],
    links: [{ rel: "canonical", href: canonical("/verse") }],
  }),
  component: VerseIndex,
});

function VerseIndex() {
  return (
    <PageChrome>
      <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
        passages
      </p>
      <h1 className="mt-3 font-display text-[clamp(1.9rem,6vw,2.9rem)] leading-[1.15] font-bold tracking-tight text-ink text-balance">
        The verses everyone knows
      </h1>
      <p className="mt-4 max-w-[60ch] text-lg leading-relaxed text-ink2">
        Eight passages, chosen from what people actually read rather than from
        what we like. Each one prints the verses around it and then deals with
        the reading it is usually given — which for several of them is the
        opposite of what the passage says.
      </p>

      <ul className="mt-10 grid gap-5 sm:grid-cols-2">
        {PASSAGES.map((passage) => (
          <li key={passage.slug}>
            <Link
              to="/verse/$slug"
              params={{ slug: passage.slug }}
              className="brutal flex h-full flex-col gap-3 p-6 transition-transform hover:-translate-y-0.5"
            >
              <span className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
                {passage.reference} · {passageMinutes(passage)} min
              </span>
              <span className="font-display text-xl leading-snug font-bold text-ink">
                {passage.h1}
              </span>
              <span className="leading-relaxed text-body">
                {passage.description}
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <section className="mt-14 border-t-[2.5px] border-ink pt-10">
        <h2 className="font-display text-2xl font-bold text-ink">
          Looking for a topic instead?
        </h2>
        <p className="mt-3 max-w-[60ch] leading-relaxed text-ink2">
          The collections are ten passages each on one theme, with a note on
          every one.
        </p>
        <ul className="mt-5 flex flex-wrap gap-3">
          {VERSE_TOPICS.map((topic) => (
            <li key={topic.slug}>
              <a
                href={versePath(topic.slug)}
                className="inline-flex rounded-pill border-[2.5px] border-ink bg-surface px-4 py-2 font-display text-sm font-bold text-ink transition-colors hover:bg-brand-wash"
              >
                {topic.h1.replace(/^Bible verses /, "")}
              </a>
            </li>
          ))}
        </ul>
      </section>
    </PageChrome>
  );
}
